/**
 * WorkstationsUpdatesPage — consola de actualizaciones del parque.
 *
 * Diseño de referencia (solo Windows: es la única plataforma con agente):
 *
 *   1. Cuatro KPIs con barra de progreso (Windows Update, pendientes,
 *      críticas y equipos que piden reinicio).
 *   2. Estado de parches (anillo) · Últimas actualizaciones · Acciones rápidas.
 *   3. Despliegues en proceso · Cobertura por sistema · Equipos por estado.
 *   4. Equipos con pendientes · Historial de despliegues · Información adicional.
 *
 * El progreso es real: el agente instala de una en una e informa de cada KB.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import StatDoughnut from './common/StatDoughnut';
import { OPS_COLORS, cardBox, boxTitle, boxHint } from './common/OpsKit';
import HelpDot from '../academy/HelpDot';
import api from '../services/api';

const ACTIVE = new Set(['pending', 'received', 'running']);
const PILL = {
  pending: { label: 'En cola', color: OPS_COLORS.info },
  received: { label: 'Recibido', color: OPS_COLORS.info },
  running: { label: 'Instalando', color: OPS_COLORS.warn },
  done: { label: 'Instalado', color: OPS_COLORS.ok },
  error: { label: 'Error', color: OPS_COLORS.bad },
};

const fecha = (v) => {
  if (!v) return '—';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? v : `${v}Z`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};
const desde = (v) => {
  if (!v) return '';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(v) ? v : `${v}Z`);
  if (Number.isNaN(d.getTime())) return '';
  const m = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  return m < 1 ? 'hace un momento' : m < 60 ? `hace ${m} min` : `hace ${Math.floor(m / 60)} h`;
};

/** Tarjeta KPI con barra de progreso, como la referencia. */
function KpiBar({ icon: Icon, color, label, value, suffix, hint, pct }) {
  return (
    <div style={{ ...cardBox, display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: `${color}1a`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <Icon style={{ width: 18, height: 18 }} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10.5, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>
            {value}{suffix ? <span style={{ fontSize: 15, fontWeight: 700 }}>{suffix}</span> : null}
          </div>
        </div>
      </div>
      <span style={{ display: 'block', height: 6, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%', background: color }} />
      </span>
      {hint ? <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{hint}</span> : null}
    </div>
  );
}

export default function WorkstationsUpdatesPage({ companyId }) {
  const [rows, setRows] = useState([]);
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [marcados, setMarcados] = useState([]);

  const loadEquipos = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError('');
    try {
      const res = await api.getWorkstations({ company_id: companyId });
      setRows((res.data || res.workstations || []).filter((w) => w.agent_id));
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar los equipos');
    } finally { setLoading(false); }
  }, [companyId]);

  const loadCommands = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await api.getAgentCommands(companyId, { limit: 40 });
      setCommands((res.commands || res.data || []).filter((c) => c.command === 'windows_update_install'));
    } catch { /* no romper la pantalla */ }
  }, [companyId]);

  useEffect(() => { loadEquipos(); loadCommands(); }, [loadEquipos, loadCommands]);

  const enCurso = useMemo(() => commands.filter((c) => ACTIVE.has(c.status)), [commands]);
  useEffect(() => {
    if (!enCurso.length) return undefined;
    const id = setInterval(loadCommands, 5000);
    return () => clearInterval(id);
  }, [enCurso.length, loadCommands]);

  const k = useMemo(() => {
    const equipos = rows.length;
    const conPend = rows.filter((w) => Number(w.updates_pending_count) > 0);
    const conCrit = rows.filter((w) => Number(w.updates_critical_count) > 0);
    const pendientes = rows.reduce((n, w) => n + (Number(w.updates_pending_count) || 0), 0);
    const criticos = rows.reduce((n, w) => n + (Number(w.updates_critical_count) || 0), 0);
    const reinicio = rows.filter((w) => w.updates_reboot_required);
    const alDia = equipos - conPend.length;
    return {
      equipos, conPend, conCrit, pendientes, criticos, reinicio, alDia,
      pctAlDia: equipos ? Math.round((alDia / equipos) * 100) : 0,
    };
  }, [rows]);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((w) => Number(w.updates_pending_count) > 0)
      .filter((w) => !t || `${w.name || ''} ${w.code || ''} ${w.ip_address || ''}`.toLowerCase().includes(t));
  }, [rows, q]);

  const ultimoPorEquipo = useMemo(() => {
    const m = new Map();
    commands.forEach((c) => { const key = String(c.ci_id); if (!m.has(key)) m.set(key, c); });
    return m;
  }, [commands]);

  /** Últimas actualizaciones instaladas: salen del resultado de los comandos. */
  const ultimas = useMemo(() => {
    const out = [];
    commands.forEach((c) => {
      (c.result?.installed || []).forEach((u) => out.push({
        id: `${c.id}-${u.title}`,
        title: u.title,
        equipo: rows.find((w) => String(w.id) === String(c.ci_id))?.name || `Equipo ${c.ci_id}`,
        when: c.executed_at || c.created_at,
      }));
    });
    return out.slice(0, 6);
  }, [commands, rows]);

  const avisoAntivirus = useMemo(() => {
    const conAv = rows.filter((w) => w.av_state === 'enabled' || w.av_state === 'ok' || w.av_product);
    return { total: rows.length, conAv: conAv.length };
  }, [rows]);

  const encolar = async (equipos, extra) => {
    if (!equipos.length) return;
    setBusy(true); setError(''); setAviso('');
    let ok = 0; let ko = 0;
    for (const w of equipos) {
      try {
        await api.enqueueAgentCommand(companyId, {
          agent_id: w.agent_id, command: 'windows_update_install',
          ci_type: 'workstation', ci_id: w.id, payload: { kbs: [], ...(extra || {}) },
        });
        ok += 1;
      } catch { ko += 1; }
    }
    setBusy(false); setMarcados([]);
    setAviso(`Operación encolada en ${ok} equipo(s)${ko ? `; ${ko} fallaron` : ''}.`);
    loadCommands();
  };

  const lanzarTodos = () => {
    if (!window.confirm(`Se instalará todo lo pendiente en ${k.conPend.length} equipo(s).\n\n¿Lanzar?`)) return;
    encolar(k.conPend);
  };
  const lanzarCriticas = () => {
    if (!window.confirm(`Solo las actualizaciones críticas en ${k.conCrit.length} equipo(s).\n\n¿Lanzar?`)) return;
    encolar(k.conCrit, { severity: 'critical' });
  };

  if (loading) return <p className="sf2-hint">Cargando la consola de actualizaciones…</p>;

  return (
    <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <ArrowPathIcon style={{ width: 22, height: 22, color: OPS_COLORS.info }} />
        <div>
          <h2 style={{ margin: 0, fontSize: 17 }}>Actualizaciones</h2>
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-muted)' }}>
            Gestión y despliegue de actualizaciones en todo el parque
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input className="list-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar equipos…" style={{ minWidth: 260 }} />
          <button type="button" className="list-btn-secondary" onClick={() => { loadEquipos(); loadCommands(); }}>
            <ArrowPathIcon style={{ width: 15, height: 15 }} /> Recargar
          </button>
          <HelpDot topic="listados.workstations_updates" label="Ayuda: actualizaciones del parque" />
        </div>
      </div>

      {error && <div className="sf2-pill sf2-pill--bad" style={{ display: 'inline-block' }}>{error}</div>}
      {aviso && !error && (
        <div className="sf2-pill sf2-pill--ok" style={{ display: 'inline-block' }}>
          <CheckCircleIcon style={{ width: 13, height: 13, marginRight: 4 }} />{aviso}
        </div>
      )}

      {/* 1 · KPIs con barra */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(215px, 1fr))', gap: 10 }}>
        <KpiBar icon={ComputerDesktopIcon} color={OPS_COLORS.ok} label="Windows Update" value={k.pctAlDia} suffix="%"
          hint={`${k.alDia} de ${k.equipos} equipos al día`} pct={k.pctAlDia} />
        <KpiBar icon={ArrowPathIcon} color={OPS_COLORS.warn} label="Equipos con actualizaciones pendientes" value={k.conPend.length}
          hint={`${k.pendientes} parches en total`} pct={k.equipos ? (k.conPend.length / k.equipos) * 100 : 0} />
        <KpiBar icon={ExclamationTriangleIcon} color={OPS_COLORS.bad} label="Con parches críticos" value={k.conCrit.length}
          hint={`${k.criticos} críticos pendientes`} pct={k.equipos ? (k.conCrit.length / k.equipos) * 100 : 0} />
        <KpiBar icon={ShieldCheckIcon} color="#8b5cf6" label="Equipos que piden reinicio" value={k.reinicio.length}
          hint="actualizaciones que lo exigen" pct={k.equipos ? (k.reinicio.length / k.equipos) * 100 : 0} />
      </div>

      {/* 2 · Estado · Últimas · Acciones */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1.5fr) minmax(240px, 0.9fr)', gap: 10 }}>
        <div style={cardBox}>
          <h3 style={boxTitle}>Estado de parches</h3>
          <p style={boxHint}>Situación del parque</p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <StatDoughnut title="" centerLabel="EQUIPOS" size={150}
              segments={[
                { label: 'Actualizado', value: Math.max(0, k.alDia), color: OPS_COLORS.ok },
                { label: 'Pendiente', value: Math.max(0, k.conPend.length - k.conCrit.length), color: OPS_COLORS.warn },
                { label: 'Crítico', value: k.conCrit.length, color: OPS_COLORS.bad },
              ]}
            />
          </div>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Últimas actualizaciones</h3>
          <p style={boxHint}>Instaladas por el agente (resultado real de cada operación)</p>
          <table className="list-table" style={{ fontSize: 11.5 }}>
            <thead><tr><th>Actualización</th><th>Equipo</th><th>Fecha</th><th>Estado</th></tr></thead>
            <tbody>
              {ultimas.map((u) => (
                <tr key={u.id}>
                  <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.title}>{u.title}</td>
                  <td>{u.equipo}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fecha(u.when)}</td>
                  <td><span className="sf2-pill sf2-pill--ok" style={{ fontSize: 10 }}>Instalada</span></td>
                </tr>
              ))}
              {ultimas.length === 0 && <tr><td colSpan={4} style={{ color: 'var(--text-muted)' }}>Sin instalaciones registradas todavía.</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Acciones rápidas</h3>
          <p style={boxHint}>Sobre el parque completo</p>
          <div style={{ display: 'grid', gap: 8 }}>
            <button type="button" className="list-btn-primary" disabled={busy || k.conPend.length === 0} onClick={lanzarTodos}>
              <RocketLaunchIcon style={{ width: 15, height: 15, marginRight: 5 }} />
              Buscar e instalar actualizaciones
            </button>
            <button type="button" className="list-btn-secondary" disabled={busy || k.conCrit.length === 0}
              title={k.conCrit.length === 0 ? 'Ningún equipo con críticas' : ''} onClick={lanzarCriticas}>
              <ExclamationTriangleIcon style={{ width: 15, height: 15, marginRight: 5 }} />
              Solo las críticas ({k.conCrit.length})
            </button>
            <button type="button" className="list-btn-secondary" disabled style={{ opacity: 0.5 }}
              title="Pendiente: programador de ventanas de mantenimiento">
              Programar despliegue
            </button>
            {k.conPend.length === 0 && (
              <div className="sf2-pill sf2-pill--ok" style={{ fontSize: 11 }}>Todo el parque al día</div>
            )}
          </div>
        </div>
      </div>

      {/* 3 · En proceso · Cobertura · Equipos por estado */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(260px, 1fr) minmax(260px, 1fr)', gap: 10 }}>
        <div style={cardBox}>
          <h3 style={boxTitle}>Despliegues en proceso</h3>
          <p style={boxHint}>Operaciones lanzadas ahora mismo</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <StatDoughnut title="" centerLabel="ACTIVOS" size={110}
              segments={[{ label: 'En curso', value: enCurso.length, color: OPS_COLORS.info }]} />
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 5, fontSize: 11.5 }}>
              {enCurso.slice(0, 4).map((c) => {
                const eq = rows.find((w) => String(w.id) === String(c.ci_id));
                const pct = Number(c.result?.progress);
                return (
                  <li key={c.id}>
                    <strong>{eq?.name || `Equipo ${c.ci_id}`}</strong>
                    <span style={{ color: 'var(--text-muted)' }}> · {c.result?.message || PILL[c.status]?.label}</span>
                    {Number.isFinite(pct) && (
                      <span style={{ display: 'block', height: 5, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', marginTop: 2 }}>
                        <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: OPS_COLORS.info }} />
                      </span>
                    )}
                  </li>
                );
              })}
              {enCurso.length === 0 && <li style={{ color: 'var(--text-muted)' }}>Nada en marcha.</li>}
            </ul>
          </div>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Cobertura por sistema</h3>
          <p style={boxHint}>Equipos al día por plataforma</p>
          {/* Solo Windows tiene agente. Los otros dos NO se pintan como anillos
              vacíos: se listan como texto, que es lo que son. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ flex: '0 0 auto' }}>
              <StatDoughnut title="" centerLabel="WIN" size={104}
                segments={[
                  { label: 'Al día', value: k.alDia, color: OPS_COLORS.ok },
                  { label: 'Pendiente', value: k.conPend.length, color: OPS_COLORS.warn },
                ]} />
            </div>
            <div style={{ minWidth: 0, fontSize: 11.5 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Windows</strong>
                <div style={{ color: 'var(--text-muted)' }}>{k.alDia} de {k.equipos} al día</div>
              </div>
              <div style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Linux · sin agente<br />
                macOS · sin agente
              </div>
            </div>
          </div>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Equipos por estado</h3>
          <p style={boxHint}>Reparto actual del parque</p>
          <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
            {[
              { label: 'Al día', value: k.alDia, color: OPS_COLORS.ok },
              { label: 'Pendientes', value: Math.max(0, k.conPend.length - k.conCrit.length), color: OPS_COLORS.warn },
              { label: 'Críticos', value: k.conCrit.length, color: OPS_COLORS.bad },
              { label: 'Piden reinicio', value: k.reinicio.length, color: '#8b5cf6' },
            ].map((s) => (
              <div key={s.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
                  <span>{s.label}</span><strong>{s.value}</strong>
                </div>
                <span style={{ display: 'block', height: 7, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', width: `${k.equipos ? (s.value / k.equipos) * 100 : 0}%`, height: '100%', background: s.color }} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 4 · Equipos · Historial · Información adicional */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(380px, 1.6fr) minmax(300px, 1.2fr) minmax(240px, 0.9fr)', gap: 10 }}>
        <div style={cardBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <h3 style={{ ...boxTitle, margin: 0, flex: 1 }}>Equipos con actualizaciones pendientes</h3>
            <button type="button" className="list-btn-secondary" disabled={busy || marcados.length === 0}
              title={marcados.length === 0 ? 'Marca algún equipo' : ''}
              onClick={() => encolar(rows.filter((w) => marcados.includes(w.id)))}
            >
              Instalar en marcados ({marcados.length})
            </button>
          </div>
          <div style={{ overflow: 'auto', maxHeight: 260 }}>
            <table className="list-table" style={{ fontSize: 11.5 }}>
              <thead><tr><th style={{ width: 26 }} /><th>Equipo</th><th>Sistema</th><th>Última comprobación</th><th>Pendientes</th><th>Estado</th></tr></thead>
              <tbody>
                {filtrados.map((w) => {
                  const cmd = ultimoPorEquipo.get(String(w.id));
                  const meta = cmd ? PILL[cmd.status] : null;
                  return (
                    <tr key={w.id}>
                      <td><input type="checkbox" checked={marcados.includes(w.id)}
                        onChange={() => setMarcados((p) => (p.includes(w.id) ? p.filter((x) => x !== w.id) : [...p, w.id]))} /></td>
                      <td style={{ fontWeight: 600 }}>{w.name}</td>
                      <td style={{ fontSize: 11 }}>{w.os_name || 'Windows'}</td>
                      <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{w.updates_state || fecha(w.inventory_updated_at)}</td>
                      <td><span className="sf2-pill sf2-pill--wa" style={{ fontSize: 10 }}>{w.updates_pending_count}</span></td>
                      <td>
                        {meta
                          ? <span className="sf2-pill" style={{ background: `${meta.color}1a`, color: meta.color, fontSize: 10 }}>{meta.label}</span>
                          : <span className="sf2-pill" style={{ fontSize: 10 }}>Pendiente</span>}
                      </td>
                    </tr>
                  );
                })}
                {filtrados.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>Ningún equipo con pendientes.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Historial de despliegues</h3>
          <p style={boxHint}>Resultado de cada operación</p>
          <table className="list-table" style={{ fontSize: 11.5 }}>
            <thead><tr><th>Fecha</th><th>Equipo</th><th>Resultado</th></tr></thead>
            <tbody>
              {commands.slice(0, 6).map((c) => {
                const eq = rows.find((w) => String(w.id) === String(c.ci_id));
                const inst = c.result?.installed?.length;
                const fail = c.result?.failed?.length;
                const okPct = inst !== undefined && (inst + (fail || 0)) > 0
                  ? Math.round((inst / (inst + (fail || 0))) * 100) : null;
                return (
                  <tr key={c.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fecha(c.executed_at || c.created_at)}</td>
                    <td>{eq?.name || `Equipo ${c.ci_id}`}</td>
                    <td>
                      {okPct !== null
                        ? <span className="sf2-pill" style={{ background: okPct === 100 ? `${OPS_COLORS.ok}1a` : `${OPS_COLORS.warn}1a`, color: okPct === 100 ? OPS_COLORS.ok : OPS_COLORS.warn, fontSize: 10 }}>{okPct}%</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{PILL[c.status]?.label || c.status}</span>}
                    </td>
                  </tr>
                );
              })}
              {commands.length === 0 && <tr><td colSpan={3} style={{ color: 'var(--text-muted)' }}>Sin historial.</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={cardBox}>
          <h3 style={boxTitle}>Información adicional</h3>
          <p style={boxHint}>Estado del parque</p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8, fontSize: 11.5 }}>
            <li style={{ display: 'flex', gap: 8 }}>
              <ShieldCheckIcon style={{ width: 16, height: 16, color: OPS_COLORS.ok, flex: '0 0 auto' }} />
              <span><strong>Antivirus</strong><br />
                <span style={{ color: 'var(--text-muted)' }}>{avisoAntivirus.conAv} de {avisoAntivirus.total} equipos con producto detectado</span>
              </span>
            </li>
            <li style={{ display: 'flex', gap: 8 }}>
              <ExclamationTriangleIcon style={{ width: 16, height: 16, color: OPS_COLORS.warn, flex: '0 0 auto' }} />
              <span><strong>Reinicio pendiente</strong><br />
                <span style={{ color: 'var(--text-muted)' }}>{k.reinicio.length} equipo(s). El agente no reinicia solo.</span>
              </span>
            </li>
            <li style={{ display: 'flex', gap: 8 }}>
              <ArrowPathIcon style={{ width: 16, height: 16, color: OPS_COLORS.info, flex: '0 0 auto' }} />
              <span><strong>Último escaneo</strong><br />
                <span style={{ color: 'var(--text-muted)' }}>{desde(rows[0]?.inventory_updated_at) || 'sin datos'}</span>
              </span>
            </li>
          </ul>
          <button type="button" className="list-btn-secondary" style={{ marginTop: 10, width: '100%' }}
            onClick={() => window.location.assign('/infrastructure/workstations/software')}
          >
            Gestionar software
          </button>
        </div>
      </div>
    </div>
  );
}
